import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom-v5-compat";
import { ScreenMax } from "styles/antTheme/Variables";
import { useMediaQuery } from "usehooks-ts";

import {
    getPlansRevisionsActiveGetV2QueryKey,
    plansRevisionsActiveGetV2,
    usePlansRevisionsByDraftIdDeleteV2,
} from "@btnet/services-btnet/handlers/plans/plans";

import { showApixErrorMessage } from "utilities/api/apix-handler";
import { routes } from "utilities/routes";

import { BTAlert } from "commonComponents/btWrappers/BTAlert/BTAlert";
import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";

export interface IPlanRevisionsBannerProps {
    setId: string;
    jobId: number;
}

/**
 * Shown on the Plans set view when the set has a pending (unfinalized) revision import. The banner is
 * driven purely by whether a draft exists for the set — applying or discarding deletes the draft, so it
 * disappears on its own with nothing to clear.
 */
export function PlanRevisionsBanner({ setId, jobId }: IPlanRevisionsBannerProps) {
    const isMobileScreen = useMediaQuery(`(max-width: ${ScreenMax.sm}px)`);
    const suppressRevisionReview = isMobileScreen;
    const navigate = useNavigate();
    const { data, refetch } = useQuery({
        queryKey: getPlansRevisionsActiveGetV2QueryKey({ setId, jobId }),
        queryFn: () => plansRevisionsActiveGetV2({ setId, jobId }),
        enabled: !!setId && !!jobId,
    });
    const discardMutation = usePlansRevisionsByDraftIdDeleteV2();

    const draftId = data?.data.draftId;
    if (!draftId) {
        return null;
    }

    const pageCount = data?.data.pageCount ?? 0;

    const handleDiscard = async () => {
        try {
            await discardMutation.mutateAsync({ draftId, params: { jobId } });
            await refetch();
        } catch (error) {
            showApixErrorMessage(error as Error, "Could not discard the revision import.");
        }
    };

    return (
        <BTAlert
            data-testid="plan-revisions-banner"
            className="mb-3"
            type="warning"
            showIcon
            message={
                !suppressRevisionReview
                    ? `A revision import is ready to review${
                          pageCount ? ` (${pageCount} pages)` : ""
                      }`
                    : "Plan revisions are ready, but are best reviewed on a larger screen."
            }
            action={
                !suppressRevisionReview && (
                    <div className="flex gap-2">
                        <BTButton
                            data-testid="plan-revisions-banner-discard"
                            type="tertiary"
                            onClick={handleDiscard}
                            loading={discardMutation.isLoading}
                        >
                            Discard
                        </BTButton>
                        <BTButton
                            data-testid="plan-revisions-banner-review"
                            type="primary"
                            onClick={() =>
                                navigate(routes.plans.getRevisionsReviewLink(draftId, jobId))
                            }
                        >
                            Review
                        </BTButton>
                    </div>
                )
            }
        />
    );
}
